import { useEffect, useRef, useState } from 'react';

/**
 * useCountUp — animates a displayed number from its previous value to `value`
 * whenever `value` changes. Pairs with the .ft-tick / .ft-tick.up/.down
 * classes in motion.css for a color flash while counting.
 *
 * Returns { display, flashing } — `display` is the formatted string to
 * render, `flashing` is true for the duration of the count (apply
 * `ft-tick ${flashing ? (value >= prev ? 'up' : 'down') : ''}`).
 */
export const useCountUp = (value, { duration = 800, decimals = 0, suffix = '', enabled = true } = {}) => {
  const [display, setDisplay] = useState(() => formatNum(value, decimals, suffix));
  const [flashing, setFlashing] = useState(false);
  const [direction, setDirection] = useState('up');
  const prevRef = useRef(value);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = prevRef.current;
    const to = value;
    prevRef.current = value;

    if (!enabled || from === to || from == null || to == null) {
      setDisplay(formatNum(to, decimals, suffix));
      return undefined;
    }

    setDirection(to >= from ? 'up' : 'down');
    setFlashing(true);
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = from + (to - from) * eased;
      setDisplay(formatNum(current, decimals, suffix));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setDisplay(formatNum(to, decimals, suffix));
        setFlashing(false);
      }
    };
    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration, decimals, suffix, enabled]);

  return { display, flashing, direction };
};

const formatNum = (n, decimals, suffix) => {
  if (n == null || Number.isNaN(n)) return '';
  return `${decimals > 0 ? n.toFixed(decimals) : Math.round(n)}${suffix}`;
};

/**
 * useReveal — returns true one tick (plus optional delay) after `active`
 * first becomes truthy, so a mount at opacity:0 gets a following render at
 * opacity:1 and the .ft-fade / .ft-stagger / .ft-seq transition actually
 * plays instead of snapping straight to its end state.
 */
export const useReveal = (active, delay = 20) => {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!active) {
      setShown(false);
      return undefined;
    }
    const t = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(t);
  }, [active, delay]);

  return shown;
};
